import {
  AddRounded,
  CloudDownloadRounded,
  EditRounded,
  FiberManualRecord,
  FileCopyRounded,
  LocalActivityRounded
} from '@mui/icons-material'
import { Box, Button, Typography, styled } from '@mui/material'
import { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { IRootState } from '../../../redux/types'
import { DetailContainer, Text } from '../styles/DetailPanelStyle'
import FavoriteButton from './buttons/FavoriteButton'
import Visibility from './frontoffice/Visibility'

type DataObject = {
  edit: string
  add: string
}

type DetailHeroPanelProps = {
  visibility?: number | null
  brand?: string | null
  title: string
  author?: string | null
  status?: number | null
  statusC?: ReactElement | null
  description?: string | null
  onClickFavorite?: (state: boolean) => void
  favoriteState?: boolean
  edit?: () => void
  event?: string | null
  eventText?: string | null
  customButton?: () => void
  customButtonText?: string | null
  add?: () => void
  share?: ReactElement | null
  hasDescription?: boolean
  copyDescription?: () => void
  download?: () => void
  data?: DataObject
}

const CustomButton = styled(Button)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  maxHeight: 40,
  height: 40,
  [theme.breakpoints.down('sm')]: {
    maxHeight: 44,
    height: 44,
    '& span p': {
      paddingLeft: 8,
      fontWeight: 500,
      fontSize: theme.typography.subtitle1.fontSize
    }
  },
  '& span p': {
    paddingLeft: 8,
    fontWeight: 500
  },
  '&.red': {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.dark
  },
  '&.redOut': {
    color: theme.palette.error.dark,
    borderColor: theme.palette.error.dark
  },
  '&.blue': {
    backgroundColor: theme.palette.info.light,
    color: theme.palette.info.main
  },
  '&.orange': {
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.dark
  },
  '&.green': {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.dark
  }
}))

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
function DetailHeroPanel({
  visibility,
  brand,
  title,
  author,
  status,
  statusC,
  description,
  onClickFavorite,
  favoriteState = false,
  edit,
  event,
  eventText,
  add,
  share,
  hasDescription = false,
  copyDescription,
  download,
  customButton,
  customButtonText,
  data = {
    edit: 'defaultEdit',
    add: 'defaultAdd'
  }
}: DetailHeroPanelProps) {
  const { t } = useTranslation(['global'])

  const { user: authUser } = useSelector((state: IRootState) => state.auth)

  return (
    <DetailContainer>
      <Box width="100%">
        <Box display="flex" justifyContent="space-between">
          {visibility != null && (
            <Box display="flex" gap={1} alignItems="center" pb="4px">
              <Typography variant="caption" color="textSecondary">
                {t('visibility')}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                |
              </Typography>
              <Visibility type={visibility} variant={1} />
            </Box>
          )}
          {brand != null && (
            <Box display="flex" alignItems="center" pb="4px">
              <Typography variant="caption" color="textSecondary" style={{ height: 14 }}>
                {brand}
              </Typography>
            </Box>
          )}
        </Box>
        <Typography sx={{ lineHeight: '1.18' }} variant="h6">
          {title}
        </Typography>
      </Box>
      <Box>
        {author != null && (
          <Box display="flex" gap={2} alignItems="center" pb="4px">
            <Text variant="caption" className="light">
              {t('author')}:
            </Text>
            <Typography variant="caption" color="textSecondary">
              {author}
            </Typography>
          </Box>
        )}
        {status != null && authUser !== null && [1, 2, 3, 5].includes(authUser.role_id) && (
          <Box display="flex" gap={2} alignItems="center" pb="4px">
            <Text variant="caption" className="blue">
              {t('status')}:
            </Text>
            <Typography variant="caption" color="textSecondary">
              {status ? t('inactive') : t('active')}
            </Typography>
          </Box>
        )}
        {statusC != null && (
          <Box display="flex" gap={2} alignItems="center" pb="4px">
            <Text variant="caption" className="blue">
              {t('status')}:
            </Text>
            <Typography variant="caption" color="textSecondary">
              {statusC}
            </Typography>
          </Box>
        )}
        {description != null && (
          <Box display="flex" gap={2} flexDirection="column" pb="4px">
            <Typography style={{ fontWeight: 500 }}>{t('description')}:</Typography>
            <Text variant="body2" className="grey5">
              {description}
            </Text>
          </Box>
        )}
      </Box>
      <Box display="flex" gap={2} flexDirection="column" width="100%" id="detailHeroDescription">
        {authUser !== null && onClickFavorite && (
          <FavoriteButton handleClick={onClickFavorite} favoriteState={favoriteState} type="button" />
        )}
        {customButton != null && (
          <CustomButton variant="text" className="blue" onClick={customButton}>
            <FiberManualRecord />
            <Typography variant="body2">{customButtonText}</Typography>
          </CustomButton>
        )}
        {edit != null && authUser !== null && [1, 2, 3, 5].includes(authUser.role_id) && (
          <CustomButton data-editheropanel={data.edit} variant="text" className="red" onClick={edit}>
            <EditRounded />
            <Typography variant="body2">{t('edit')}</Typography>
          </CustomButton>
        )}
        {event != null && (
          <CustomButton variant="text" className="orange" href={event}>
            <LocalActivityRounded />
            <Typography variant="body2">{eventText}</Typography>
          </CustomButton>
        )}
        {add != null && authUser !== null && [1, 2, 3, 5].includes(authUser.role_id) && (
          <CustomButton data-addheropanel={data.add} variant="outlined" className="redOut" onClick={add}>
            <AddRounded />
            <Typography variant="body2">{t('admin.courses.add-lesson')}</Typography>
          </CustomButton>
        )}
        {share != null && <>{share}</>}
        {hasDescription && (
          <CustomButton variant="text" className="orange" onClick={copyDescription}>
            <FileCopyRounded />
            <Typography variant="body2">{t('copy-description')}</Typography>
          </CustomButton>
        )}
        {download != null && (
          <CustomButton variant="text" className="blue" onClick={download}>
            <CloudDownloadRounded />
            <Typography variant="body2">{t('download')}</Typography>
          </CustomButton>
        )}
      </Box>
    </DetailContainer>
  )
}

export default DetailHeroPanel
